import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface AnalyticsDateSelectorProps {
    value: string;
    onChange: (value: string) => void;
}

const OPTIONS = [
    { value: 'day', label: 'Ultime 24 ore' },
    { value: 'week', label: 'Ultimi 7 giorni' },
    { value: 'month', label: 'Ultimi 30 giorni' },
    { value: 'year', label: 'Ultimi 12 mesi' },
    { value: 'ever', label: 'Sempre' },
];

const AnalyticsDateSelector: React.FC<AnalyticsDateSelectorProps> = ({ value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Chiudi quando clicchi fuori
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedLabel = OPTIONS.find(o => o.value === value)?.label || 'Periodo';

    return (
        <div className="relative" ref={containerRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-gray-700 font-medium transition-all shadow-sm min-w-[200px] ${isOpen ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200 hover:bg-gray-50'}`}
            >
                <div className="flex items-center gap-2">
                    <Calendar size={18} className="text-gray-400" />
                    <span className="text-sm">{selectedLabel}</span>
                </div>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-12 right-0 z-50 w-56 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden py-1"
                    >
                        {OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                onClick={() => {
                                    onChange(option.value);
                                    setIsOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors ${value === option.value ? 'font-bold text-gray-900 bg-gray-50/50' : 'text-gray-600'}`}
                            >
                                {option.label}
                                {value === option.value && <Check size={14} className="text-gray-900" />}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AnalyticsDateSelector;